import {useId, startTransition, useState} from 'react'
import {CircleIcon, StopIcon} from '@primer/octicons-react'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useAnalytics} from '@github-ui/use-analytics'
import {useQueryClient} from '@github-ui/react-query'
import {Banner, BranchName, Button, Flash, NavList} from '@primer/react'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'

import styles from './StackSection.module.css'
import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {CenteredLoadingSpinner} from './common/CenteredLoadingSpinner'
import type {
  StackSectionStatusType,
  StackSectionEntryType,
} from '../../helpers/merge-box-status-calculator/stack-section-status'
import {
  useStackRebaseMutation,
  useStackRebaseStatus,
  type StackRebasePullRequestStatus,
} from '../../hooks/mutations/use-stack-rebase-mutation'
import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import type {AliveChannels as MergeBoxAliveChannels} from '../../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../../page-data/payloads/status-checks'
import {useMergeabilityLiveUpdates} from '../../hooks/use-mergeability-live-updates'
import {useStackMergeabilityQuery} from '../../page-data/loaders/use-stack-mergeability-page-data'
import {useMergeMethodContext} from '../../contexts/MergeMethodContext'
import {EntryStatusLabel} from './stack-section/EntryStatusLabel'
import {StackStatusIcon} from './stack-section/StatusIcon'
import {EntryStatusIcon} from './stack-section/EntryStatusIcon'

export function StackSection({
  pullRequestId,
  status,
  loadingEntryCount,
  entries,
  baseRefName,
  showRebaseButton = true,
}: {
  pullRequestId: string
  status: StackSectionStatusType
  loadingEntryCount: number
  entries: StackSectionEntryType[]
  baseRefName?: string
  showRebaseButton?: boolean
}) {
  const stackSectionAriaId = useId()
  const [stackExpanded, setStackExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:stackExpanded`,
    status !== 'PASSED',
  )
  const {isRebasing, status: rebaseState} = useStackRebaseStatus()
  const [hideRebaseSummary, setHideRebaseSummary] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const showRebaseSummary = Object.keys(rebaseState).length > 0 && !isRebasing && !hideRebaseSummary

  const {sendAnalyticsEvent} = useAnalytics()
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()
  const {pathname} = useLocation()

  // Preserve the current tab (e.g. /changes, /commits) when navigating between stacked PRs
  const tabSuffix = getPullRequestTabSuffix(pathname)

  const onSuccessfulRebase = async () => {
    await queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
  }

  const handleRebaseError = (e: Error) => {
    setErrorMessage(e.message)
  }

  const {mutate: rebaseStack} = useStackRebaseMutation({
    onSuccess: onSuccessfulRebase,
    onError: handleRebaseError,
  })

  const handleRebaseStack = () => {
    if (isRebasing) return
    setErrorMessage(null)
    rebaseStack({entries})
    sendAnalyticsEvent('conflicts_section.update_branch', 'MERGEBOX_CONFLICTS_SECTION_UPDATE_BRANCH_BUTTON')
  }

  const otherEntriesToMerge = entries.filter(e => e.position === 'BEFORE' && e.pull.state !== 'MERGED')

  // Compute which BEFORE entries can merge immediately (e.g. all before entries have 'OK' status)
  const canMergeMap = new Map<number, boolean>()
  let allBelowPass = true
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i]!
    if (e.position !== 'BEFORE' || e.pull.state === 'MERGED') continue
    canMergeMap.set(e.pull.number, e.status === 'OK' && allBelowPass)
    if (e.status !== 'OK') {
      allBelowPass = false
    }
  }

  let title
  let subtitle
  let anyPullsAreLoading = false
  const isFirstUnmergedPRInStack = otherEntriesToMerge.length === 0

  switch (true) {
    case isRebasing && Object.keys(rebaseState).length > 0: {
      const count = Object.values(rebaseState).length
      title = 'Rebasing stack…'
      subtitle = `Rebasing ${count} pull request${count === 1 ? '' : 's'}.`
      break
    }
    case isRebasing:
      title = 'Rebasing stack…'
      subtitle = 'Starting rebase.'
      break
    case (status === 'FAILED' || status === 'BLOCKED') && isFirstUnmergedPRInStack:
      title = 'Unable to merge the first pull request in the stack'
      subtitle = 'This pull request cannot be merged.'
      break
    case status === 'FAILED' || status === 'BLOCKED':
      title = 'Unable to merge as a stack'
      subtitle = 'Some of the pull requests in this stack cannot be merged.'
      break
    case status === 'PASSED' && isFirstUnmergedPRInStack:
      title = 'Able to merge the first pull request in the stack'
      subtitle = 'Only this pull request will be merged.'
      break
    case status === 'PASSED':
      title = 'Able to merge as a stack'
      subtitle = `Merging this pull request will also merge ${otherEntriesToMerge.length} pull request${otherEntriesToMerge.length === 1 ? '' : 's'} below it.`
      break
    case status === 'LOADING_MERGE_STATE':
      anyPullsAreLoading = true
      title = 'Loading stack status...'
      subtitle = `${loadingEntryCount} of ${entries.length} pull requests loading`
      break
    default:
      title = 'Could not determine stack status'
      subtitle = 'Refresh the page to try again.'
  }

  return (
    <section
      aria-label="Stacked pull requests"
      aria-describedby={stackSectionAriaId}
      className="border-bottom color-border-subtle"
    >
      <MergeBoxSectionHeader
        headerId={stackSectionAriaId}
        title={title}
        subtitle={subtitle}
        icon={
          isRebasing || anyPullsAreLoading ? (
            <CenteredLoadingSpinner className={styles.StackSectionLoadingSpinner} />
          ) : (
            <StackStatusIcon status={status} isFirstUnmergedPRInStack={isFirstUnmergedPRInStack} />
          )
        }
        {...(showRebaseButton
          ? {
              rightSideContent: (
                <Button loading={isRebasing} loadingAnnouncement="Rebasing stack" onClick={handleRebaseStack}>
                  Rebase stack
                </Button>
              ),
            }
          : {
              expandableProps: {
                ariaLabel: stackExpanded ? 'Collapse stack details' : 'Expand stack details',
                isExpanded: stackExpanded,
                onToggle: () => {
                  setStackExpanded(!stackExpanded)
                },
              },
            })}
      />
      <MergeBoxExpandable isExpanded={showRebaseButton || stackExpanded}>
        <NavList className={styles.stackNavList}>
          {entries.map(e => (
            <NavList.Item
              key={e.pull.number}
              href={`${e.pull.url}${tabSuffix}`}
              aria-current={e.position === 'CURRENT'}
            >
              <NavList.LeadingVisual>
                <EntryStatusIcon entry={e} rebaseState={rebaseState[e.pull.number]} />
              </NavList.LeadingVisual>
              <span className={e.position === 'AFTER' ? 'fgColor-muted' : ''}>{e.pull.title}</span>{' '}
              <span className="fgColor-muted">#{e.pull.number}</span>
              <NavList.TrailingVisual>
                <EntryStatusLabel
                  entry={e}
                  rebaseState={rebaseState[e.pull.number]}
                  allBelowCanMerge={canMergeMap.get(e.pull.number) ?? false}
                />
              </NavList.TrailingVisual>
              {!!e.channels && <EntryMergeabilityUpdateListener basePageDataUrl={e.pull.url} {...e.channels} />}
            </NavList.Item>
          ))}
          {baseRefName && (
            <li className={styles.branchItem}>
              <span className={styles.branchItemContent}>
                <CircleIcon size={12} className="fgColor-muted" />
                <BranchName as="span">{baseRefName}</BranchName>
              </span>
            </li>
          )}
        </NavList>
      </MergeBoxExpandable>
      {showRebaseSummary && (
        <RebaseSummaryBanner rebaseState={rebaseState} onDismiss={() => setHideRebaseSummary(true)} />
      )}
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
    </section>
  )
}

function EntryMergeabilityUpdateListener({
  basePageDataUrl,
  ...channels
}: {
  basePageDataUrl: string
} & MergeBoxAliveChannels &
  StatusChecksAliveChannels) {
  const {mergeMethod} = useMergeMethodContext()
  const {refetch} = useStackMergeabilityQuery({basePageDataUrl, mergeMethod, enabled: false})
  useMergeabilityLiveUpdates({
    channels,
    refetchQuery: () => {
      startTransition(() => {
        refetch()
      })
    },
    throttleTime: 5000,
  })
  return null
}

function RebaseSummaryBanner({
  rebaseState,
  onDismiss,
}: {
  rebaseState: Record<number, StackRebasePullRequestStatus>
  onDismiss: () => void
}) {
  const states = Object.values(rebaseState)
  const succeededCount = states.filter(s => s === 'SUCCEEDED').length
  const failedCount = states.filter(s => s === 'FAILED' || s === 'CONFLICTED').length

  if (states.length === 0) return null

  const allSucceeded = failedCount === 0
  const variant = allSucceeded ? 'success' : 'critical'

  let description: string
  if (allSucceeded) {
    description = `Successfully rebased ${succeededCount} pull request${succeededCount === 1 ? '' : 's'}.`
  } else {
    const parts: string[] = []
    if (succeededCount > 0) {
      parts.push(`${succeededCount} pull request${succeededCount === 1 ? '' : 's'} rebased successfully.`)
    }
    parts.push(`${failedCount} pull request${failedCount === 1 ? '' : 's'} failed to rebase.`)
    description = parts.join(' ')
  }

  return (
    <Banner
      className="tmp-m-3"
      aria-label="Rebase summary"
      variant={variant}
      title="Rebase complete"
      hideTitle
      description={description}
      onDismiss={onDismiss}
    />
  )
}
